import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { LoadingSpinner, Severity } from "@velaro/velaro-component-lib";
import { ChatWindow } from "../../types";

interface Props {
  chatWindow: ChatWindow;
}

interface ShopifyStore {
  id: number;
  storeName: string;
  domain: string;
  widgetDeployed: boolean;
}

interface BCStore {
  id: number;
  displayName: string;
  storeName: string;
  storeUrl: string;
  widgetDeployed: boolean;
  scriptTagUuid: string | null;
}

interface MagentoConfig {
  id: number;
  storeUrl: string;
  enabled: boolean;
}

interface WooStatus {
  isConnected: boolean;
  storeUrl: string;
}

type DeployStatus = "idle" | "deploying" | "removing";

// ── Embed code helper ────────────────────────────────────────────────────────

function EmbedCodeSection({ siteId, chatWindowId }: { siteId: number; chatWindowId: number }) {
  const [copied, setCopied] = useState(false);
  const isStaging = window.location.hostname.includes("staging") || window.location.hostname === "localhost";
  const cdn = isStaging
    ? "https://cdn-us-east-test-velaro.azurewebsites.net"
    : "https://cdn.velaro.com";
  const src = `${cdn}/messenger/loader.js?siteId=${siteId}${chatWindowId > 0 ? `&chatWindowId=${chatWindowId}` : ""}`;
  const snippet = `<script src="${src}" async></script>`;

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-semibold text-slate-800">Embed code</span>
        <span className="text-xs bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">Any website</span>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Copy and paste this script tag into the <code>&lt;head&gt;</code> or before the closing <code>&lt;/body&gt;</code> tag of your site.
      </p>
      <div className="flex gap-2 items-start">
        <code className="flex-1 text-xs bg-white border border-slate-200 rounded p-3 break-all text-slate-700 font-mono select-all">
          {snippet}
        </code>
        <button
          onClick={copy}
          className="shrink-0 text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition"
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}

// ── Store deploy card ────────────────────────────────────────────────────────

function StoreCard({
  logo,
  name,
  subtitle,
  deployed,
  status,
  onDeploy,
  onRemove,
  disabled
}: {
  logo: React.ReactNode;
  name: string;
  subtitle: string;
  deployed: boolean;
  status: DeployStatus;
  onDeploy(): void;
  onRemove(): void;
  disabled?: boolean;
}) {
  const busy = status !== "idle";

  return (
    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-lg">
          {logo}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">{name}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {busy ? (
          <LoadingSpinner />
        ) : deployed ? (
          <>
            <span className="text-xs text-green-700 bg-green-100 rounded-full px-2.5 py-0.5 font-medium">
              ✓ Deployed
            </span>
            <button
              onClick={onRemove}
              className="text-xs text-red-500 hover:text-red-700 transition"
            >
              Remove
            </button>
          </>
        ) : (
          <button
            onClick={onDeploy}
            disabled={disabled}
            className="text-xs px-3 py-1.5 rounded-lg bg-cornflower-blue-600 hover:bg-cornflower-blue-700 text-white transition disabled:opacity-40"
          >
            Deploy to store
          </button>
        )}
      </div>
    </div>
  );
}

// ── Static platform card (plugin-based, no API deploy) ───────────────────────

function PluginCard({
  logo,
  name,
  subtitle,
  linkLabel,
  linkHref,
  instruction
}: {
  logo: React.ReactNode;
  name: string;
  subtitle: string;
  linkLabel: string;
  linkHref: string;
  instruction: string;
}) {
  return (
    <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-lg">
          {logo}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">{name}</div>
          <div className="text-xs text-slate-400 truncate">{subtitle}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <span className="text-xs text-slate-500 hidden sm:block">{instruction}</span>
        <a
          href={linkHref}
          target="_blank"
          rel="noreferrer"
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition"
        >
          {linkLabel}
        </a>
      </div>
    </div>
  );
}

// ── Main Deploy tab ──────────────────────────────────────────────────────────

export default function Deploy({ chatWindow }: Props) {
  const api = useApi();
  const toast = useToastAlert();

  const [shopifyStores, setShopifyStores] = useState<ShopifyStore[]>([]);
  const [bcStores, setBcStores] = useState<BCStore[]>([]);
  const [magentoConfigs, setMagentoConfigs] = useState<MagentoConfig[]>([]);
  const [woo, setWoo] = useState<WooStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Per-store deploy status: key = "shopify-{id}" | "bc-{id}"
  const [deployStatus, setDeployStatus] = useState<Record<string, DeployStatus>>({});

  const setStatus = (key: string, s: DeployStatus) =>
    setDeployStatus((prev) => ({ ...prev, [key]: s }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shopifyResp, bcResp, magentoResp, wooResp] = await Promise.allSettled([
        api.messaging.get(`Shopify/Integrations`),
        api.admin.get(`BigCommerce/Configs`),
        api.admin.get(`Magento/Configs`),
        api.admin.get(`WooCommerce/Status`)
      ]);

      if (shopifyResp.status === "fulfilled" && shopifyResp.value.ok) {
        const data = await shopifyResp.value.json();
        setShopifyStores(
          (Array.isArray(data) ? data : []).map((s: any) => ({
            id: s.id,
            storeName: s.storeName ?? s.domain ?? `Store ${s.id}`,
            domain: s.domain ?? "",
            widgetDeployed: !!s.scriptTagId
          }))
        );
      }

      if (bcResp.status === "fulfilled" && bcResp.value.ok) {
        const data = await bcResp.value.json();
        setBcStores(
          (Array.isArray(data) ? data : []).map((c: any) => ({
            id: c.id,
            displayName: c.displayName,
            storeName: c.storeName ?? c.displayName,
            storeUrl: c.storeUrl ?? "",
            widgetDeployed: !!c.widgetDeployed,
            scriptTagUuid: c.scriptTagUuid ?? null
          }))
        );
      }

      if (magentoResp.status === "fulfilled" && magentoResp.value.ok) {
        const data = await magentoResp.value.json();
        setMagentoConfigs(Array.isArray(data) ? data : []);
      }

      if (wooResp.status === "fulfilled" && wooResp.value.ok) {
        const data = await wooResp.value.json();
        setWoo({ isConnected: data?.isConnected ?? false, storeUrl: data?.storeUrl ?? "" });
      }
    } catch {
      // silently ignore — sections just stay empty
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // ── Shopify deploy / remove ─────────────────────────────────────────────

  const deployShopify = async (store: ShopifyStore) => {
    const key = `shopify-${store.id}`;
    setStatus(key, "deploying");
    try {
      const resp = await api.messaging.post(
        `Shopify/Integrations/${store.id}/deploy-widget?chatWindowId=${chatWindow.id}`
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Deploy failed" }));
        toast.displayToast(Severity.Error, err.error ?? "Deploy failed");
        return;
      }
      setShopifyStores((prev) =>
        prev.map((s) => s.id === store.id ? { ...s, widgetDeployed: true } : s)
      );
      toast.displayToast(Severity.Success, `Widget deployed to ${store.storeName}`);
    } catch {
      toast.displayToast(Severity.Error, "Deploy failed");
    } finally {
      setStatus(key, "idle");
    }
  };

  const removeShopify = async (store: ShopifyStore) => {
    const key = `shopify-${store.id}`;
    setStatus(key, "removing");
    try {
      await api.messaging.delete(`Shopify/Integrations/${store.id}/deploy-widget`);
      setShopifyStores((prev) =>
        prev.map((s) => s.id === store.id ? { ...s, widgetDeployed: false } : s)
      );
    } catch {
      toast.displayToast(Severity.Error, "Remove failed");
    } finally {
      setStatus(key, "idle");
    }
  };

  // ── BigCommerce deploy / remove ─────────────────────────────────────────

  const deployBC = async (store: BCStore) => {
    const key = `bc-${store.id}`;
    setStatus(key, "deploying");
    try {
      const resp = await api.admin.post(
        `BigCommerce/Configs/${store.id}/deploy-widget?chatWindowId=${chatWindow.id}`
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Deploy failed" }));
        toast.displayToast(Severity.Error, err.error ?? "Deploy failed");
        return;
      }
      setBcStores((prev) =>
        prev.map((s) => s.id === store.id ? { ...s, widgetDeployed: true } : s)
      );
      toast.displayToast(Severity.Success, `Widget deployed to ${store.displayName}`);
    } catch {
      toast.displayToast(Severity.Error, "Deploy failed");
    } finally {
      setStatus(key, "idle");
    }
  };

  const removeBC = async (store: BCStore) => {
    const key = `bc-${store.id}`;
    setStatus(key, "removing");
    try {
      await api.admin.delete(`BigCommerce/Configs/${store.id}/deploy-widget`);
      setBcStores((prev) =>
        prev.map((s) => s.id === store.id ? { ...s, widgetDeployed: false } : s)
      );
    } catch {
      toast.displayToast(Severity.Error, "Remove failed");
    } finally {
      setStatus(key, "idle");
    }
  };

  const hasAnyStore =
    shopifyStores.length > 0 ||
    bcStores.length > 0 ||
    magentoConfigs.length > 0 ||
    woo?.isConnected;

  return (
    <div className="panel bg-white rounded-2xl shadow-md border p-6 max-w-3xl mx-auto">
      <div className="text-lg font-medium mb-1">Deploy</div>
      <p className="text-sm text-slate-500 mb-6">
        Push this chat window to your connected storefronts, or copy the embed code for any website.
      </p>

      {/* Embed code — always shown */}
      <div className="mb-6">
        <EmbedCodeSection siteId={chatWindow.siteId} chatWindowId={chatWindow.id} />
      </div>

      {/* Connected storefronts */}
      <div className="mb-2">
        <div className="text-sm font-semibold text-slate-700 mb-3">Connected storefronts</div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : !hasAnyStore ? (
          <div className="text-sm text-slate-400 text-center py-8 border border-dashed border-slate-200 rounded-xl">
            No storefronts connected yet.{" "}
            <a href="/integrations" className="text-cornflower-blue-600 underline">
              Connect an integration →
            </a>
          </div>
        ) : (
          <div className="space-y-3">
            {shopifyStores.map((store) => (
              <StoreCard
                key={`shopify-${store.id}`}
                logo="🛍️"
                name={store.storeName || store.domain}
                subtitle={`Shopify · ${store.domain}`}
                deployed={store.widgetDeployed}
                status={deployStatus[`shopify-${store.id}`] ?? "idle"}
                onDeploy={() => deployShopify(store)}
                onRemove={() => removeShopify(store)}
              />
            ))}

            {bcStores.map((store) => (
              <StoreCard
                key={`bc-${store.id}`}
                logo="🔵"
                name={store.displayName}
                subtitle={`BigCommerce · ${store.storeUrl}`}
                deployed={store.widgetDeployed}
                status={deployStatus[`bc-${store.id}`] ?? "idle"}
                onDeploy={() => deployBC(store)}
                onRemove={() => removeBC(store)}
              />
            ))}

            {magentoConfigs.filter((c) => c.enabled).map((cfg) => (
              <PluginCard
                key={`magento-${cfg.id}`}
                logo="🔶"
                name="Magento / Adobe Commerce"
                subtitle={cfg.storeUrl}
                linkLabel="Configure module"
                linkHref={`${cfg.storeUrl}/admin`}
                instruction="Set Site ID in the Velaro module settings"
              />
            ))}

            {woo?.isConnected && (
              <PluginCard
                logo="🟣"
                name="WooCommerce"
                subtitle={woo.storeUrl}
                linkLabel="Configure plugin"
                linkHref={`${woo.storeUrl}/wp-admin/admin.php?page=velaro`}
                instruction="Widget is injected by the Velaro WooCommerce plugin"
              />
            )}
          </div>
        )}
      </div>

      {/* No-code / plugin installs — WordPress and GTM */}
      <div className="mt-6">
        <div className="text-sm font-semibold text-slate-700 mb-3">No-code installs</div>
        <div className="space-y-3">
          {/* Google Tag Manager — always show */}
          <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-base font-bold text-slate-600">
                GTM
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">Google Tag Manager</div>
                <div className="text-xs text-slate-400">Paste one script, deploy to any site — no developer needed</div>
              </div>
            </div>
            <Link
              to="/integrations/google-tag-manager"
              className="shrink-0 text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition"
            >
              Set up GTM →
            </Link>
          </div>

          {/* WordPress — hide when WooCommerce is already handling it */}
          {!woo?.isConnected && (
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-4 gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-lg">
                  🔷
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800">WordPress</div>
                  <div className="text-xs text-slate-400">Add the embed code via the WPCode plugin — no theme editing required</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-400 hidden sm:block">Copy embed code above, then:</span>
                <a
                  href="https://wordpress.org/plugins/insert-headers-and-footers/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition"
                >
                  WPCode plugin →
                </a>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Marketplace links — shown if installed via marketplace */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="text-xs text-slate-400">
          Installed via an app marketplace?{" "}
          {shopifyStores.length > 0 && (
            <a
              href={`https://${shopifyStores[0].domain}/admin/apps`}
              target="_blank"
              rel="noreferrer"
              className="text-cornflower-blue-600 underline mr-3"
            >
              Manage on Shopify →
            </a>
          )}
          {bcStores.length > 0 && bcStores[0].storeUrl && (
            <a
              href={`${bcStores[0].storeUrl}/manage/marketplace/apps`}
              target="_blank"
              rel="noreferrer"
              className="text-cornflower-blue-600 underline"
            >
              Manage on BigCommerce →
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
